import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";

/**
 * L'EXPLICATION D'UN RÉGLAGE, DANS UNE BULLE — et non plus sous sa ligne.
 *
 * ⚠️ CE QUE ÇA CORRIGE : 69 réglages portent une aide, 117 caractères en médiane et
 * jusqu'à 300. Imprimées sous chaque ligne, elles doublaient la hauteur du panneau — un
 * organisateur qui cherche un interrupteur qu'il connaît déjà déroulait des paragraphes
 * qu'il a lus la première fois et jamais relus. La bulle rend l'aide À LA DEMANDE : le
 * panneau redevient une liste de réglages, l'explication reste à un survol.
 *
 * ⚠️ `position: fixed`, ET C'EST OBLIGATOIRE ICI. Le panneau défile (`overflow-y: auto`)
 * et la carte du `SpotEditor` est bornée en hauteur : une bulle en `absolute` s'y
 * ferait couper net dès qu'elle dépasse. C'est le même arbitrage — et le même remède —
 * que `.spot`, qui se positionne déjà ainsi.
 *
 * ⚠️ ET ELLE BASCULE AU-DESSUS QUAND LE BAS MANQUE, après MESURE. La hauteur dépend du
 * texte : de deux lignes à dix. Devinée, elle sortait de l'écran sur les aides longues,
 * c'est-à-dire précisément celles qu'on ouvre.
 *
 * ⚠️ LE TEXTE EST DANS LE DOM EN PERMANENCE, hors écran, et la bulle visible est
 * `aria-hidden`. Un tooltip qui n'existe qu'une fois ouvert n'est jamais annoncé : le
 * lecteur d'écran lit `aria-describedby` au moment où le bouton prend le focus, donc
 * avant que quoi que ce soit soit apparu. Deux nœuds pour un texte, mais l'aide se lit
 * au clavier comme à la souris.
 *
 * Displayed strings are French: the organiser is a French speaker.
 */

/** Largeur de la bulle. Le panneau fait 380 px, la carte du spot 300. */
const WIDTH = 240;
/** Distance gardée du bouton et des bords de la fenêtre. */
const MARGE = 8;

type Position = { left: number; top: number };

export function Help({ text, subject }: { text: string; subject: string }) {
  const id = useId();
  const button = useRef<HTMLButtonElement>(null);
  const bubble = useRef<HTMLSpanElement>(null);

  /**
   * DEUX CAUSES D'OUVERTURE, ET ELLES NE SE FERMENT PAS PAREIL.
   *
   * ⚠️ LE CLIC ÉPINGLE, LE SURVOL NON — et sans cela l'aide était inatteignable au
   * doigt : un écran tactile n'a pas de survol, donc la bulle ne se serait ouverte que
   * pour ceux qui ont une souris. Épinglée, elle survit au départ du pointeur et se
   * ferme par un second appui, par `Échap`, ou en touchant ailleurs.
   */
  const [hover, setHover] = useState(false);
  const [pinned, setPinned] = useState(false);
  /**
   * ⚠️ `Échap` DOIT FERMER MÊME QUAND LE FOCUS RESTE, et sans ce troisième état il ne
   * fermait rien au clavier : le focus maintient `survol`, donc dés-épingler ne suffisait
   * pas — la bulle se rouvrait dans le même souffle. C'est pourtant ce que le motif ARIA
   * exige, et pour une raison concrète : au clavier on ne peut pas « sortir le pointeur »,
   * donc `Échap` est le SEUL moyen de dégager une bulle qui recouvre ce qu'on lit.
   *
   * Il se lève dès qu'on redemande l'aide — survol, retour du focus, ou appui sur le
   * bouton — sinon il faudrait quitter la ligne et y revenir pour la revoir.
   */
  const [hidden, setHidden] = useState(false);
  const open = (hover || pinned) && !hidden;

  /** Redemander l'aide lève le masque : c'est le geste qui dit « je la reveux ». */
  const reask = () => setHidden(false);

  const [at, setAt] = useState<Position | null>(null);

  /**
   * Mesurée après peinture, puis placée — la hauteur dépend de la longueur du texte.
   * Tant que la mesure n'est pas faite, la bulle est rendue transparente plutôt que
   * masquée : masquée, elle n'aurait aucune hauteur à mesurer.
   */
  useLayoutEffect(() => {
    if (!open) return setAt(null);
    const anchor = button.current?.getBoundingClientRect();
    const box = bubble.current;
    if (!anchor || !box) return;

    const hauteur = box.offsetHeight;
    /* Alignée sur le bord DROIT du bouton — le panneau est à droite de l'écran, donc
       une bulle qui s'étend vers la gauche reste dans la fenêtre. Bornée quand même :
       le `SpotEditor` peut ouvrir ses lignes n'importe où. */
    const left = Math.max(
      MARGE,
      Math.min(anchor.right - WIDTH, window.innerWidth - WIDTH - MARGE),
    );
    const below = anchor.bottom + MARGE;
    const top =
      below + hauteur > window.innerHeight - MARGE
        ? Math.max(MARGE, anchor.top - hauteur - MARGE)
        : below;

    setAt({ left, top });
  }, [open, text]);

  /**
   * ⚠️ `Échap` FERME, ET LE CLIC EXTÉRIEUR AUSSI. Une bulle épinglée qui ne se ferme
   * qu'en retrouvant son propre bouton reste par-dessus le panneau dès qu'on regarde
   * ailleurs — et au clavier, elle devient un cul-de-sac.
   */
  useEffect(() => {
    if (!open) return;
    const escape = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setPinned(false);
      // Et masquée, faute de quoi le focus la rouvrirait aussitôt — voir `masque`.
      setHidden(true);
    };
    const dehors = (e: MouseEvent) => {
      if (!button.current?.contains(e.target as Node)) setPinned(false);
    };
    document.addEventListener("keydown", escape);
    document.addEventListener("mousedown", dehors);
    return () => {
      document.removeEventListener("keydown", escape);
      document.removeEventListener("mousedown", dehors);
    };
  }, [open]);

  /* Le panneau défile sous une bulle posée en `fixed` : elle resterait accrochée au
     vide. On la ferme plutôt que de la faire suivre — c'est une aide, pas une carte
     qu'on manipule. `true` attrape aussi le défilement DANS le panneau, qui n'atteint
     pas la fenêtre. */
  useEffect(() => {
    if (!open) return;
    const closed = () => {
      setHover(false);
      setPinned(false);
    };
    window.addEventListener("scroll", closed, true);
    return () => window.removeEventListener("scroll", closed, true);
  }, [open]);

  return (
    <>
      <button
        ref={button}
        type="button"
        className={`aide${pinned ? " aide--on" : ""}`}
        /* Le nom du réglage est REPRIS dans l'intitulé : au clavier, on tabule d'une
           aide à l'autre, et « Aide » seul ne dirait pas de quoi. */
        aria-label={`Aide : ${subject}`}
        aria-describedby={id}
        onClick={() => {
          reask();
          setPinned((e) => !e);
        }}
        onPointerEnter={() => {
          reask();
          setHover(true);
        }}
        onPointerLeave={() => setHover(false)}
        onFocus={() => {
          reask();
          setHover(true);
        }}
        onBlur={() => setHover(false)}
      >
        <span aria-hidden="true">?</span>
      </button>

      {/* Ce que le lecteur d'écran lit, toujours présent — voir l'en-tête. */}
      <span id={id} className="a11y">
        {text}
      </span>

      {open && (
        <span
          ref={bubble}
          className="aide__t"
          /* Déjà lu par `aria-describedby` sur le bouton : annoncé deux fois, le texte
             serait répété à chaque survol. */
          aria-hidden="true"
          style={{
            left: at?.left ?? 0,
            top: at?.top ?? 0,
            // Invisible tant qu'on ne l'a pas mesurée, mais bien peinte : une bulle
            // masquée n'a pas de hauteur, donc rien à mesurer.
            visibility: at ? "visible" : "hidden",
          }}
        >
          {text}
        </span>
      )}
    </>
  );
}
